import React from 'react';
import { MessageSquare } from 'lucide-react';
import { RECORD_VIEW_CONFIG } from '../constants';

interface ViewTabsProps {
  view: string;
  viewCounts: Record<string, number>;
  onChange: (view: string) => void;
  idBase: string;
}

const ViewTabs = React.memo(function ViewTabs({
  view,
  viewCounts,
  onChange,
  idBase,
}: ViewTabsProps) {
  const entries = Object.entries(RECORD_VIEW_CONFIG);
  const keys = entries.map(([key]) => key);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = keys.indexOf(view);
    if (currentIndex === -1) return;
    let nextIndex = currentIndex;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % keys.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + keys.length) % keys.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = keys.length - 1;
    } else {
      return;
    }
    e.preventDefault();
    const nextKey = keys[nextIndex];
    onChange?.(nextKey);
    requestAnimationFrame(() => {
      document.getElementById(`${idBase}-tab-${nextKey}`)?.focus();
    });
  };

  return (
    <div
      className="px-4 pb-3 flex gap-2 overflow-x-auto"
      role="tablist"
      aria-label="Content views"
      aria-orientation="horizontal"
    >
      {entries.map(([key, config]) => {
        const Icon = config.icon || MessageSquare;
        const active = key === view;
        const count = key === 'chat' ? viewCounts?.chat : viewCounts?.[key];
        const tabId = `${idBase}-tab-${key}`;
        const panelId = `${idBase}-panel-${key}`;
        const label = typeof count === 'number' ? `${config.label} (${count})` : config.label;
        return (
          <button
            key={key}
            role="tab"
            aria-selected={active}
            aria-controls={panelId}
            aria-label={label}
            id={tabId}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange?.(key)}
            onKeyDown={handleKeyDown}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition ${
              active
                ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
            } focus:outline-2 focus:outline-blue-500`}
            type="button"
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="font-medium">{config.label}</span>
            {typeof count === 'number' && (
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  active ? 'bg-white/20 text-white' : 'bg-white text-gray-800'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
});

export default ViewTabs;
